/** Public audit sheet view — opened from shared links (email / WhatsApp).
 * Shows the audit table for the shared duration + Excel download. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileDown, Loader2, ShieldCheck } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${Number(n || 0).toLocaleString()}`;

export default function AuditShareView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/public/audit-share/${token}`)
      .then(({ data: d }) => setData(d))
      .catch((e) => setErr(e.response?.data?.detail || "Audit link not found"));
  }, [token]);

  if (err) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="rounded-2xl border border-rose-200 bg-white p-8 text-center shadow" data-testid="audit-share-error">
          <div className="text-lg font-bold text-rose-600">Link unavailable</div>
          <p className="mt-1 text-sm text-slate-500">{err}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 size={28} className="animate-spin text-slate-300" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="audit-share-page">
      <header className="bg-[#0F2042] px-6 py-8 text-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-white/95 px-2 py-1"><img src="/projexino-logo.png" alt="Projexino" className="h-6 object-contain" /></div>
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider"><ShieldCheck size={11} /> Shared audit sheet</span>
            </div>
            <h1 className="mt-3 font-display text-2xl font-bold sm:text-3xl" data-testid="audit-share-title-view">{data.title}</h1>
            <p className="mt-1 text-sm text-white/70">Duration: {data.from_date} → {data.to_date} · {data.rows.length} entries</p>
          </div>
          <a href={`${API}/api/public/audit-share/${token}/download.xlsx`} data-testid="audit-share-download"
            className="inline-flex items-center gap-2 rounded-full bg-[#F97316] px-6 py-3 text-sm font-bold text-white shadow-xl hover:bg-orange-600">
            <FileDown size={15} /> Download Excel
          </a>
        </div>
      </header>

      <main className="mx-auto -mt-4 max-w-5xl px-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-2xl bg-white p-4 shadow ring-1 ring-emerald-200"><div className="text-[10px] font-bold uppercase text-emerald-700">Total credited</div><div className="mt-1 text-xl font-bold text-emerald-700" data-testid="audit-share-credit">{inr(data.totals.credit)}</div></div>
          <div className="rounded-2xl bg-white p-4 shadow ring-1 ring-rose-200"><div className="text-[10px] font-bold uppercase text-rose-700">Total debited</div><div className="mt-1 text-xl font-bold text-rose-700" data-testid="audit-share-debit">{inr(data.totals.debit)}</div></div>
          <div className="rounded-2xl bg-white p-4 shadow ring-1 ring-slate-200"><div className="text-[10px] font-bold uppercase text-slate-500">Net</div><div className="mt-1 text-xl font-bold text-[#0F2042]" data-testid="audit-share-net">{inr(data.totals.net)}</div></div>
        </div>

        <div className="mt-4 overflow-x-auto rounded-2xl bg-white shadow ring-1 ring-slate-200">
          <table className="w-full text-sm" data-testid="audit-share-table">
            <thead className="bg-[#0F2042] text-left text-[10px] uppercase tracking-wider text-white">
              <tr>{data.columns.map((c) => <th key={c.key} className="px-3 py-2.5">{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan={data.columns.length} className="px-3 py-10 text-center text-xs text-slate-400">No entries in this duration.</td></tr>
              ) : data.rows.map((r, i) => (
                <tr key={i} className={`border-t border-slate-100 ${i % 2 ? "bg-slate-50/60" : ""}`}>
                  {data.columns.map((c) => (
                    <td key={c.key} className={`px-3 py-2 text-xs ${c.key === "amount" ? (r.type === "Debit" ? "font-bold text-rose-600" : "font-bold text-emerald-700") : "text-slate-700"}`}>
                      {c.key === "amount" ? inr(r.amount) : c.key === "type" ? (
                        <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${r.type === "Debit" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>{r.type}</span>
                      ) : String(r[c.key] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-6 text-center text-xs text-slate-400">© {new Date().getFullYear()} Projexino Solutions Pvt Ltd · Generated {new Date(data.created_at).toLocaleDateString()}</p>
      </main>
    </div>
  );
}
